import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Plus, Search, Upload, Users } from "lucide-react";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { LeadBulkImportModal } from "@/components/admin/marketing/lead-bulk-import-modal";
import { LeadCreateModal } from "@/components/admin/marketing/lead-create-modal";
import { LeadDetailDrawer } from "@/components/admin/marketing/lead-detail-drawer";
import {
  LEAD_TABLE_COLUMNS,
  LeadTableRow,
} from "@/components/admin/marketing/leads/lead-table-row";
import {
  ENTITY_TYPES,
  FILTER_STAGES,
  LEAD_SOURCES,
  LEADS_QUERY_ROOT,
  readApiError,
  type LeadListResponse,
} from "@/components/admin/marketing/leads/types";
import { useLeadLabels } from "@/components/admin/marketing/leads/use-lead-labels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Marketing → Leads.
 *
 * List + filters (country / stage / source / entity / search) + pagination,
 * create-lead modal, bulk CSV import and the detail drawer (consent, opt-outs,
 * record consent, GDPR erasure).
 *
 * Endpoints (apps/api/src/routes/admin-marketing.ts):
 *   GET  /api/admin/marketing/leads?country=&stage=&source=&entityType=&search=&limit=&offset=
 *        → { leads: [row + adsConsent + linkedToAccount], total, limit, offset }
 *   POST /api/admin/marketing/leads — never writes consent; answers `ignoredFields`
 *   /leads/:id (GET, PATCH, POST …/consent, DELETE) — see LeadDetailDrawer
 *
 * DB toggle: the list is a React Query keyed on `dbTarget`, so a Local↔Prod
 * switch never shows the other database's rows.
 */

const PAGE_SIZE = 25;

export const Route = createFileRoute("/_authenticated/marketing-leads")({
  component: LeadsPage,
});

function LeadsPage() {
  const { t } = useLingui();
  const labels = useLeadLabels();
  const queryClient = useQueryClient();
  const { visibleCountries: countries } = useAdminCountries();
  const { country: headerCountry, setCountry: setHeaderCountry, dbTarget } = useAdminContext();
  const selectedCountry = headerCountry ?? "";
  const setSelectedCountry = (code: string) => setHeaderCountry(code === "" ? null : code);

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of countries) {
      if (c.totalProviders > 0) map[c.code] = c.totalProviders;
    }
    return map;
  }, [countries]);

  const [offset, setOffset] = useState(0);
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [entityType, setEntityType] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [importKey, setImportKey] = useState(0);
  // The id outlives `drawerOpen` so the drawer keeps its content while it slides out.
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openLead = (id: string) => {
    setOpenLeadId(id);
    setDrawerOpen(true);
  };

  const listQuery = useQuery({
    queryKey: [
      ...LEADS_QUERY_ROOT,
      dbTarget,
      selectedCountry,
      stage,
      source,
      entityType,
      search,
      offset,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.set("country", selectedCountry);
      if (stage) params.set("stage", stage);
      if (source) params.set("source", source);
      if (entityType) params.set("entityType", entityType);
      if (search) params.set("search", search);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      return apiFetch.get<LeadListResponse>(`/api/admin/marketing/leads?${params}`);
    },
    staleTime: 30_000,
  });

  const leads = listQuery.data?.leads ?? [];
  const total = listQuery.data?.total ?? 0;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: LEADS_QUERY_ROOT });
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setOffset(0);
  };

  const listError = (() => {
    if (!listQuery.isError) return null;
    const info = readApiError(listQuery.error);
    return info ? `HTTP ${info.status}` : (listQuery.error as Error).message;
  })();

  const selectClass =
    "border-border bg-background min-h-11 w-full rounded-lg border px-3 text-sm sm:w-auto";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            <Trans>Leads</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>Lead pipeline · {total} total in current view</Trans>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => {
              setImportKey((k) => k + 1);
              setShowImport(true);
            }}
          >
            <Upload className="size-4" aria-hidden />
            <Trans>Import CSV</Trans>
          </Button>
          <Button
            className="min-h-11"
            onClick={() => {
              setCreateKey((k) => k + 1);
              setShowCreate(true);
            }}
          >
            <Plus className="size-4" aria-hidden />
            <Trans>New lead</Trans>
          </Button>
        </div>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={(code) => {
          setSelectedCountry(code);
          setOffset(0);
        }}
      />

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:flex sm:flex-wrap sm:items-end">
          <div className="sm:min-w-64 sm:flex-1">
            <label htmlFor="leads-search" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Search</Trans>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search
                  className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                  aria-hidden
                />
                <input
                  id="leads-search"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder={t`Email, name, business…`}
                  className="border-border bg-background min-h-11 w-full rounded-lg border py-1.5 pr-3 pl-8 text-sm outline-none"
                />
              </div>
              <Button className="min-h-11" onClick={handleSearch}>
                <Trans>Search</Trans>
              </Button>
            </div>
          </div>
          <div>
            <label htmlFor="leads-stage" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Stage</Trans>
            </label>
            <select
              id="leads-stage"
              value={stage}
              onChange={(e) => {
                setStage(e.target.value);
                setOffset(0);
              }}
              className={selectClass}
            >
              <option value="">{t`All stages`}</option>
              {FILTER_STAGES.map((s) => (
                <option key={s} value={s}>
                  {labels.stageLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="leads-source" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Source</Trans>
            </label>
            <select
              id="leads-source"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setOffset(0);
              }}
              className={selectClass}
            >
              <option value="">{t`All sources`}</option>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {labels.sourceLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="leads-entity" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Entity</Trans>
            </label>
            <select
              id="leads-entity"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setOffset(0);
              }}
              className={selectClass}
            >
              <option value="">{t`All entity types`}</option>
              {ENTITY_TYPES.map((s) => (
                <option key={s} value={s}>
                  {labels.entityLabel(s)}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="border-border/50 overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border/50 bg-muted/30 border-b">
                <th className="px-3 py-2 text-left font-semibold">
                  <Trans>Lead</Trans>
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  <Trans>Stage</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold sm:table-cell">
                  <Trans>Consent</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold md:table-cell">
                  <Trans>Entity</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold lg:table-cell">
                  <Trans>Source</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                  <Trans>Score</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                  <Trans>Events</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold xl:table-cell">
                  <Trans>Last activity</Trans>
                </th>
                <th className="w-12 xl:hidden">
                  <span className="sr-only">
                    <Trans>Details</Trans>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isPending ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-border/30 border-b last:border-0">
                    <td colSpan={LEAD_TABLE_COLUMNS} className="px-3 py-2">
                      <Skeleton className="h-10" />
                    </td>
                  </tr>
                ))
              ) : listError ? (
                <tr>
                  <td
                    colSpan={LEAD_TABLE_COLUMNS}
                    className="text-destructive-strong p-8 text-center text-sm"
                  >
                    {listError}
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td
                    colSpan={LEAD_TABLE_COLUMNS}
                    className="text-muted-foreground p-8 text-center"
                  >
                    <Users className="mx-auto mb-2 size-7 opacity-40" aria-hidden />
                    <p className="text-sm">
                      <Trans>No leads in current filter</Trans>
                    </p>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => <LeadTableRow key={lead.id} lead={lead} onOpen={openLead} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground text-sm">
            <Trans>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="min-h-11 min-w-11"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              aria-label={t`Previous page`}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              variant="outline"
              className="min-h-11 min-w-11"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              aria-label={t`Next page`}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}

      <LeadCreateModal
        key={`create-${createKey}`}
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={() => {
          setOffset(0);
          refresh();
        }}
      />
      <LeadBulkImportModal
        key={`import-${importKey}`}
        open={showImport}
        onOpenChange={setShowImport}
        onImported={() => {
          setOffset(0);
          refresh();
        }}
      />
      <LeadDetailDrawer
        leadId={openLeadId}
        open={drawerOpen && openLeadId !== null}
        onOpenChange={setDrawerOpen}
        onUpdated={refresh}
      />
    </div>
  );
}
